import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { parseAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type {
  DataViewNotification,
  DataViewNotificationType,
} from 'cadenza/workbook/notifications/workbook-notifications';
import { createNotification } from 'cadenza/workbook/notifications/workbook-notifications';

import i18n from './color-classification-validation.properties';

export function collectClassificationAttributeErrors(
  viewId: ViewId,
  dataView: DataViewDto,
  activeAttributesById: Record<ActiveAttributeId, WorkbookAttributeDto>,
  objecttypeByAttributeId: Record<AttributeId, Objecttype>,
) {
  const colorClassificationActiveAttributeId = dataView.attributes['COLOR_CLASSIFICATION' as AttributeBundleName];
  // if classification exists ...
  if (Array.isArray(colorClassificationActiveAttributeId) && colorClassificationActiveAttributeId.length > 0) {
    const activeAttribute = activeAttributesById[colorClassificationActiveAttributeId[0]];
    if (activeAttribute.status !== 'OK') {
      // validate only when there is no error found yet)
      return [];
    }
    return validateColorClassificationAttributeConfiguration(activeAttribute, objecttypeByAttributeId) ?
        []
      : [createInvalidColorClassificationNotification(viewId, dataView.id)];
  }
  return [];
}

function validateColorClassificationAttributeConfiguration(
  attribute: WorkbookAttributeDto,
  objecttypeByAttributeId: Record<AttributeId, Objecttype>,
) {
  if (attribute.status !== 'OK') {
    // ignore case when attribute is invalid by status
    return true;
  }
  const attributeValueColorRules = attribute.attributeColorSettings?.colorRules;
  // if there are no values defined there is no invalid configuration
  if (attributeValueColorRules.length === 0) {
    return true;
  }

  const attributeNamesPresentOnValues = attributeValueColorRules[0].attributeValues.map((val) => val.name);

  // check if each rule has the same number of values
  const sameNumberOfValues = attributeValueColorRules?.every(
    (rule) => rule.attributeValues.length === attributeNamesPresentOnValues.length,
  );
  if (!sameNumberOfValues) {
    return false;
  }
  // check if each rule values are configured with the same attributes
  attributeNamesPresentOnValues.forEach((attrName) => {
    const valueExists = attributeValueColorRules?.every((rule) =>
      rule.attributeValues.some((v) => v.name === attrName),
    );
    if (!valueExists) {
      return false;
    }
  });

  const attributeBaseId = attribute.baseId!;
  const objecttype = objecttypeByAttributeId[attributeBaseId];
  const relationGroupsWhereAttributeIsPresent = objecttype.attributeRelationGroups.filter((group) => {
    return group.nameAttribute === attributeBaseId;
  });
  if (relationGroupsWhereAttributeIsPresent.length > 0) {
    const attributeNamesThatShouldBePresentOnValues = relationGroupsWhereAttributeIsPresent
      .flatMap((group) => [...group.idAttributes, group.nameAttribute])
      .map((attrBaseId) => parseAttributeId(attrBaseId!)[1]);

    if (attributeNamesThatShouldBePresentOnValues.length !== attributeNamesPresentOnValues.length) {
      return false;
    }
    attributeNamesPresentOnValues.forEach((attrName) => {
      const sameAttributes = attributeNamesThatShouldBePresentOnValues.some((attr) => attr === attrName);
      if (!sameAttributes) {
        return false;
      }
    });
  }

  return true;
}

function createInvalidColorClassificationNotification(viewId: ViewId, dataViewId: DataViewId): DataViewNotification {
  const notificationType: DataViewNotificationType = 'INVALID_COLOR_CLASSIFICATION';

  return createNotification('ERROR', 'WORKBOOK_UPDATE', notificationType, viewId, {
    title: i18n('invalid-classification.title'),
    detail: i18n('invalid-classification'),
    dataViewId,
  }) as DataViewNotification;
}
